<template>
  <div class="competency-container">
    <div class="competency-header">
      <span class="label-text">Select Competencies relevant to the Learning Activity</span>
      <div v-if="isOtjAiEnabled" class="competency-actions">
        <button
          v-if="aiSuggestedCompetencyIds.length > 0"
          class="btn btn-outlined btn-sm filter-button"
          @click="toggleFilterSuggestions"
          :class="{ active: showOnlySuggestions }"
          :title="showOnlySuggestions ? 'Show all competencies' : 'Show only AI-suggested competencies'"
        >
          <i class="fas" :class="showOnlySuggestions ? 'fa-filter' : 'fa-filter'" aria-hidden="true"></i>
          {{ showOnlySuggestions ? 'Show All' : `Show Suggested (${aiSuggestedCompetencyIds.length})` }}
        </button>
        <button
          class="btn btn-outlined btn-sm"
          @click="autoSelectCompetencies"
          :disabled="!validDescription || loadingSuggestions"
          :title="validDescription ? 'Automatically select suggested competencies' : 'Please enter what you learned first'"
        >
          <img v-if="!loadingSuggestions" :src="chatbotImage" class="chatbot-icon-small" alt="KATE bot avatar"/>
          <i v-if="loadingSuggestions" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
          {{ loadingSuggestions ? 'Generating...' : 'Suggest Competencies with KATE' }}
        </button>
      </div>
    </div>
    <template v-if="competenciesReady && competencies.length > 0">
      <div class="selected-competencies">
        <span class="selected-competencies-label">Currently selected:</span>
        <k-ai-generated-disclosure
          v-if="aiSuggestedCompetencyIds.length > 0"
          variant="competencies"
          class="competency-ai-disclosure"
        />
        <div class="competency-pills" v-if="selectedCompetencies.length > 0">
          <k-status-pill
            v-for="competency in visibleCompetencies"
            :key="competency"
            :status-value="competency"
          />
          <button
            v-if="shouldShowToggleButton"
            class="show-more-btn"
            @click="toggleShowAll"
          >
            {{ showAll ? 'Show less' : `+ ${hiddenCompetencies.length} more` }}
            <i :class="showAll ? 'fas fa-angle-up' : 'fas fa-angle-down'" aria-hidden="true"></i>
          </button>
        </div>
        <div v-else>None selected</div>
      </div>
      <k-editable-table
        v-model="displayedCompetencies"
        :headers="tableHeaders"
        :max="10"
        :initial-sort-state="initialSortState"
      />
    </template>
    <div v-else>
      <span class="label-text">No competencies found for this programme</span>
    </div>
  </div>
</template>

<script>
import KEditableTable from '@base-components/k-editable-table.vue';
import KStatusPill from '@base-components/k-status-pill.vue';
import { competencySortBy } from '@utils/sort-table-competencies';
import { importImage } from '@utils/dynamic-image-importer';
import KAiGeneratedDisclosure from '@base-components/ai/k-ai-generated-disclosure.vue';

export default {
  name: 'OtjCompetencySelector',
  components: {
    KEditableTable,
    KStatusPill,
    KAiGeneratedDisclosure,
  },
  props: {
    programmeId: {
      type: Number,
      required: true,
    },
    learningDescription: {
      type: String,
      default: '',
    },
    learningActivityType: {
      type: Object,
      default: null,
    },
    otjData: {
      type: Object,
      default: undefined,
    },
    showModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      competencies: [],
      competenciesReady: false,
      showAll: false,
      loadingSuggestions: false,
      aiSuggestedCompetencyIds: [],
      showOnlySuggestions: false,
      displayedCompetencies: [],
      isUpdatingCompetencies: false,
      initialSortState: {
        headerKey: 'label',
        ascending: true,
      },
    };
  },
  computed: {
    validDescription() {
      return this.learningDescription && this.learningDescription.trim().length > 0;
    },
    tableHeaders() {
      return [
        {
          key: 'checkbox',
          title: '',
          editable: true,
          type: 'boolean',
          required: true,
          sortable: true,
        },
        {
          key: 'label',
          title: 'Label',
          editable: false,
          sortable: true,
          filterable: true,
          sortBy: competencySortBy,
          type: 'string',
        },
        {
          key: 'category',
          title: 'Category',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
        {
          key: 'description',
          title: 'Description',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
      ];
    },
    selectedCompetencies() {
      return this.competencies.filter(competency => competency.checkbox);
    },
    sortedCompetencyLabels() {
      return this.selectedCompetencies
        .map(competency => competency.label)
        .sort((a, b) => {
          const aSort = competencySortBy(a);
          const bSort = competencySortBy(b);
          if (aSort[0] !== bSort[0]) return aSort[0] - bSort[0];
          return aSort[1] - bSort[1];
        });
    },
    visibleCompetencies() {
      const maxVisible = 15;
      return this.showAll ? this.sortedCompetencyLabels : this.sortedCompetencyLabels.slice(0, maxVisible);
    },
    hiddenCompetencies() {
      const maxVisible = 15;
      return this.showAll ? [] : this.sortedCompetencyLabels.slice(maxVisible);
    },
    shouldShowToggleButton() {
      const maxVisible = 15;
      return this.sortedCompetencyLabels.length > maxVisible;
    },
    chatbotImage() {
      return importImage('icons/kate-bot-chat-colours.svg');
    },

    isOtjAiEnabled() {
      return this.$featureFlags && this.$featureFlags.isEnabled('otj-ai-enhancements');
    },
  },
  watch: {
    showModal: {
      handler(newVal) {
        if (newVal && this.programmeId) {
          this.getCompetencies();
        }
      },
      // Runs on creation so competencies still load when the modal is mounted already open
      immediate: true,
    },
    competencies: {
      handler() {
        if (this.competencies && Array.isArray(this.competencies)) {
          // Set flag to prevent displayedCompetencies watcher from syncing back
          this.isUpdatingCompetencies = true;
          this.updateDisplayedCompetencies();
          this.$nextTick(() => {
            this.isUpdatingCompetencies = false;
          });
        }
      },
      immediate: true,
    },
    showOnlySuggestions() {
      // Set flag to prevent displayedCompetencies watcher from syncing back
      this.isUpdatingCompetencies = true;
      this.updateDisplayedCompetencies();
      this.$nextTick(() => {
        this.isUpdatingCompetencies = false;
      });
    },
    aiSuggestedCompetencyIds() {
      // Set flag to prevent displayedCompetencies watcher from syncing back
      this.isUpdatingCompetencies = true;
      this.updateDisplayedCompetencies();
      this.$nextTick(() => {
        this.isUpdatingCompetencies = false;
      });
    },
    displayedCompetencies: {
      handler(newDisplayedCompetencies) {
        // Skip if we're in the middle of a programmatic update
        if (this.isUpdatingCompetencies) {
          return;
        }
        if (!newDisplayedCompetencies || newDisplayedCompetencies.length === 0) {
          return;
        }
        if (!this.competencies || !Array.isArray(this.competencies)) {
          return;
        }

        const displayedMap = new Map(newDisplayedCompetencies.map(c => [c.id, c]));

        // Check if any checkbox values actually changed to avoid unnecessary updates
        let hasChanges = false;
        const updatedCompetencies = this.competencies.map(comp => {
          const displayed = displayedMap.get(comp.id);
          if (displayed && typeof displayed.checkbox === 'boolean' && displayed.checkbox !== comp.checkbox) {
            hasChanges = true;
            return {
              ...comp,
              checkbox: displayed.checkbox,
            };
          }
          return comp;
        });

        // Only update if there are actual changes to prevent infinite loops
        if (hasChanges) {
          this.isUpdatingCompetencies = true;
          this.competencies = updatedCompetencies;
          this.$nextTick(() => {
            this.isUpdatingCompetencies = false;
          });
        }
      },
      deep: true,
    },
    selectedCompetencies: {
      handler(newSelected) {
        this.$emit('update:selectedCompetencies', newSelected);
      },
      immediate: true,
    },
  },
  methods: {
    getCompetencies() {
      this.$Loading.minimal();
      this.$logger.info('Fetching competencies', true);
      this.$http.get(`/api/curriculum/programmes/${this.programmeId}/competencies/scores`)
        .then(response => {
          this.competencies = response.data.competencies.map(comp => ({
            ...comp,
            checkbox: comp.checkbox !== undefined ? comp.checkbox : false,
          }));
          this.competenciesReady = true;

          if (this.otjData && this.otjData.competencies) {
            this.markSelectedCompetencies(this.otjData.competencies);
          }
        })
        .catch(error => {
          this.$logger.error('Error fetching competencies', undefined, error);
          this.competenciesReady = true;
        })
        .then(() => {
          this.$Loading.finish();
        });
    },
    updateDisplayedCompetencies() {
      if (!this.competencies || !Array.isArray(this.competencies)) {
        this.displayedCompetencies = [];
        return;
      }
      if (this.showOnlySuggestions && this.aiSuggestedCompetencyIds.length > 0) {
        this.displayedCompetencies = this.competencies
          .filter(comp => this.aiSuggestedCompetencyIds.includes(comp.id))
          .map(comp => ({
            ...comp,
            checkbox: comp.checkbox !== undefined ? comp.checkbox : false,
          }));
      } else {
        this.displayedCompetencies = this.competencies.map(comp => ({
          ...comp,
          checkbox: comp.checkbox !== undefined ? comp.checkbox : false,
        }));
      }
    },
    markSelectedCompetencies(draftCompetencies) {
      if (!this.otjData || !this.otjData.competencies || !this.competencies) {
        return;
      }

      const selectedIds = draftCompetencies.map(c => c.id);
      const updatedCompetencies = [];

      this.competencies.forEach(competency => {
        const isSuggested = this.aiSuggestedCompetencyIds.includes(competency.id);
        updatedCompetencies.push({
          ...competency,
          checkbox: selectedIds.includes(competency.id),
          isSuggested,
        });
      });

      this.competencies = updatedCompetencies;
    },
    autoSelectCompetencies() {
      if (!this.validDescription || !this.competenciesReady || this.competencies.length === 0) {
        return;
      }

      this.loadingSuggestions = true;
      this.$logger.info('Requesting AI competency suggestions', { programmeId: this.programmeId }, true);

      const competenciesList = this.competencies.map(comp => ({
        id: comp.id,
        label: comp.label,
        category: comp.category,
        description: comp.description,
      }));

      const learningActivityTypeName = this.learningActivityType?.name || null;

      const payload = {
        learning_description: this.learningDescription.trim(),
        learning_activity_type: learningActivityTypeName,
        competencies: competenciesList,
      };

      this.$http.post(`/api/chatbot/otj/suggest-competencies/${this.programmeId}`, payload)
        .then(response => {
          const suggestedIds = response.data.suggested_competency_ids || [];
          this.$logger.info('AI suggestions received', { count: suggestedIds.length }, true);

          const previousSuggestedIds = this.aiSuggestedCompetencyIds || [];
          const manuallySelectedIds = this.competencies
            .filter(comp => comp.checkbox === true && !previousSuggestedIds.includes(comp.id))
            .map(comp => comp.id);

          const updatedCompetencies = this.competencies.map(competency => {
            const isNewlySuggested = suggestedIds.includes(competency.id);
            const wasPreviouslySuggested = previousSuggestedIds.includes(competency.id);
            const isManuallySelected = manuallySelectedIds.includes(competency.id);

            let shouldBeChecked;
            if (isManuallySelected) {
              shouldBeChecked = true;
            } else if (isNewlySuggested) {
              shouldBeChecked = true;
            } else if (wasPreviouslySuggested) {
              shouldBeChecked = false;
            } else {
              shouldBeChecked = competency.checkbox === true;
            }

            return {
              ...competency,
              checkbox: shouldBeChecked,
              isSuggested: isNewlySuggested,
            };
          });

          this.isUpdatingCompetencies = true;
          this.competencies = updatedCompetencies;
          this.aiSuggestedCompetencyIds = suggestedIds;
          this.updateDisplayedCompetencies();

          this.$nextTick(() => {
            this.isUpdatingCompetencies = false;
          });

          if (suggestedIds.length > 0) {
            this.$ktoast.success(`Selected ${suggestedIds.length} suggested competenc${suggestedIds.length > 1 ? 'ies' : 'y'}.`);
          } else {
            this.$ktoast.info('No relevant competencies were found for your learning description.');
          }
        })
        .catch(err => {
          this.$logger.error('Error getting AI suggestions', { programmeId: this.programmeId }, err);
          this.$ktoast.showError('Failed to get AI suggestions. Please try again or select competencies manually.');
        })
        .then(() => {
          this.loadingSuggestions = false;
        });
    },
    toggleFilterSuggestions() {
      this.showOnlySuggestions = !this.showOnlySuggestions;
    },
    toggleShowAll() {
      this.showAll = !this.showAll;
    },
  },
};
</script>

<style scoped>
.competency-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.competency-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.label-text {
  font-weight: 500;
  color: var(--kate-type-primary);
}

.competency-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.chatbot-icon-small {
  width: 20px;
  height: 20px;
}

.filter-button.active {
  background-color: var(--kate-type-primary);
  color: var(--kate-type-light);
}

.selected-competencies {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: var(--kate-type-secondary);
  gap: 6px;
}

.competency-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
}

.show-more-btn {
  background: none;
  border: none;
  color: var(--kate-type-link);
  cursor: pointer;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 4px;
  text-decoration: underline;
}

.show-more-btn:hover {
  color: var(--kate-type-link-hover);
}
</style>

<style>
[data-theme="dark"] .competency-container .selected-competencies-label,
[data-theme="legacy"] .competency-container .selected-competencies-label {
  color: var(--kate-type-white) !important;
}

[data-theme="light"] .competency-container .selected-competencies-label {
  color: var(--kate-type-primary) !important;
}
</style>
